import { useEffect, useState } from 'react';
import { InfoIcon } from 'lucide-react';
import { useGetCurrentWorkspaceId } from '@tinct/core-workspace';
import {
  useGetOrganizationById,
  useGetSnippetVerificationResult,
  useUpdateSnippetAnalyticsMode,
  useVerifyOrganizationSnippet,
} from '@tinct/core-api';
import type { AnalyticsMode } from '@tinct/core-api-models';
import { Alert, AlertDescription } from '@tinct/web-components';
import {
  SnippetStatusCard,
  type SnippetCampaign,
} from '../../components/snippet-status-card';
import {
  SnippetCheckCard,
  type SnippetCheckState,
} from '../../components/snippet-check-card';
import { SnippetInstallationCard } from '../../components/snippet-installation-card';
import { SnippetAnalyticsModeCard } from '../../components/snippet-analytics-mode-card';
import { LayoutContent, LayoutHeader, LayoutTopbar } from '@tinct/web-layout';
import { useSnippetVerificationJobAlert } from './hooks/use-snippet-verification-job-alert';

const POLL_INTERVAL_MS = 3000;
const PENDING_JOB_ID = '__pending__';

export function SnippetConfigurationPage() {
  const workspaceId = useGetCurrentWorkspaceId();

  // `expectedJobId` tracks which verification the UI is following.
  // - PENDING_JOB_ID: mutation in flight, no real id yet
  // - <uuid>: the verification job the user kicked off (or adopted on mount)
  // - null: not tracking (initial state, after a retry, or after onError)
  const [expectedJobId, setExpectedJobId] = useState<string | null>(null);
  // Sticky "user pressed retry" override so the check card returns to its default state.
  const [retried, setRetried] = useState(false);

  const { data: organization } = useGetOrganizationById(
    {
      id: workspaceId ?? '',
      fields: {
        domain: null,
        snippetStatus: {
          fields: {
            id: null,
            domain: null,
            status: null,
            analyticsMode: null,
            error: null,
            lastVerificationJob: {
              fields: {
                id: null,
                status: null,
                progress: null,
              },
            },
            lastVerifiedAt: null,
            activatedAt: null,
          },
        },
      },
    },
    {
      enabled: !!workspaceId,
      refetchInterval: (query) => {
        const status = query.state.data?.snippetStatus?.lastVerificationJob?.status;
        return status === 'running' || status === 'queued' ? POLL_INTERVAL_MS : false;
      },
    },
  );

  const snippetStatus = organization?.snippetStatus;
  const currentJobId = snippetStatus?.lastVerificationJob?.id ?? null;
  const verificationJobStatus = snippetStatus?.lastVerificationJob?.status;
  const isJobActive = verificationJobStatus === 'running' || verificationJobStatus === 'queued';

  // Adopt the in-flight job on first sight (page reload while a verification is running).
  // Render-phase setState — fires at most once because the guard immediately becomes false.
  if (expectedJobId === null && currentJobId !== null && isJobActive) {
    setExpectedJobId(currentJobId);
  }

  const isJobMine =
    expectedJobId !== null &&
    (expectedJobId === currentJobId || expectedJobId === PENDING_JOB_ID);

  const isVerifying = expectedJobId === PENDING_JOB_ID || (isJobMine && isJobActive);

  const checkState: SnippetCheckState = (() => {
    if (retried) return 'default';
    if (isVerifying) return 'in-progress';
    if (!isJobMine) return 'default';
    if (verificationJobStatus === 'completed') {
      return snippetStatus?.status === 'active' ? 'success' : 'error';
    }
    if (verificationJobStatus === 'failed') return 'error';
    return 'default';
  })();

  const errorMessage = checkState === 'error' ? snippetStatus?.error ?? null : null;

  const { data: verificationResult } = useGetSnippetVerificationResult(
    { organizationId: workspaceId ?? '' },
    {
      enabled: !!workspaceId && !isVerifying,
    },
  );

  // Map campaign verification results for the status card
  const campaignResults: SnippetCampaign[] = (verificationResult?.campaigns ?? []).map((c) => ({
    name: c.campaign?.name ?? '',
    url: c.url ?? '',
    status: c.status === 'active' ? 'active' : 'inactive',
  }));

  // Job alert for background tracking — re-register whenever we own a real job id.
  const { start: startJobAlert } = useSnippetVerificationJobAlert();
  useEffect(() => {
    if (isJobMine && currentJobId) {
      startJobAlert(currentJobId);
    }
  }, [isJobMine, currentJobId, startJobAlert]);

  const { mutate: verifySnippet } = useVerifyOrganizationSnippet({
    onMutate: () => {
      setRetried(false);
      setExpectedJobId(PENDING_JOB_ID);
    },
    onSuccess: (job) => {
      setExpectedJobId(job.id ?? PENDING_JOB_ID);
    },
    onError: () => {
      setExpectedJobId(null);
    },
  });

  const { mutate: updateAnalyticsMode, isPending: isSavingAnalyticsMode } =
    useUpdateSnippetAnalyticsMode();

  const handleSaveAnalyticsMode = (analyticsMode: AnalyticsMode) => {
    if (!workspaceId) return;
    updateAnalyticsMode({
      organizationId: workspaceId,
      data: { analyticsMode },
    });
  };

  const handleVerify = (url: string) => {
    if (!workspaceId) return;
    verifySnippet({
      organizationId: workspaceId,
      data: { domain: url, campaigns: true },
    });
  };

  const handleVerifyAgain = () => {
    if (!workspaceId || !snippetStatus?.domain) return;
    verifySnippet({
      organizationId: workspaceId,
      data: { domain: snippetStatus.domain, campaigns: true },
    });
  };

  const handleRetry = () => {
    setRetried(true);
    setExpectedJobId(null);
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
  };

  return (
    <div>
      <header className="sticky top-0 z-30">
        <LayoutTopbar />
      </header>
      <LayoutContent>
        {/* Page Header */}
        <LayoutHeader
          pageTitle="Your tracking snippet"
          pageDescription="One-time installation to activate personalization"
        />

        {/* Main Content */}
        <div className="flex max-w-4xl flex-col gap-8">
          {/* Snippet Status Card */}
          {snippetStatus?.status && (
            <SnippetStatusCard
              status={snippetStatus.status}
              campaigns={campaignResults}
              lastVerified={snippetStatus.lastVerifiedAt}
              activeSince={snippetStatus.activatedAt}
              domain={snippetStatus.domain}
              onVerifyAgain={handleVerifyAgain}
              isVerifying={isVerifying}
            />
          )}

          {/* Verify Installation Card */}
          <SnippetCheckCard
            state={checkState}
            progress={snippetStatus?.lastVerificationJob?.progress ?? 0}
            errorMessage={errorMessage}
            defaultUrl={organization?.domain ?? undefined}
            onVerify={handleVerify}
            onRetry={handleRetry}
          />

          {/* Installation Method Card */}
          <SnippetInstallationCard
            snippetId={snippetStatus?.id ?? undefined}
            snippetUrlPrefix={import.meta.env['VITE_SNIPPET_URL_PREFIX']}
            onCopyCode={handleCopyCode}
          />

          {/* Analytics Mode Card */}
          <SnippetAnalyticsModeCard
            value={snippetStatus?.analyticsMode}
            isSaving={isSavingAnalyticsMode}
            onSave={handleSaveAnalyticsMode}
          />

          {/* Info Alert */}
          <Alert variant="important">
            <InfoIcon className="size-4" />
            <AlertDescription>
              One snippet for all campaigns. This tracking snippet works across
              your entire website and enables personalization for all your
              campaigns. You only need to install it once.
            </AlertDescription>
          </Alert>
        </div>
      </LayoutContent>
    </div>
  );
}
